/**
 * Validações puras das telas de login/cadastro (S1–S6).
 * Cada função devolve o código do erro para a tela escolher o texto — o
 * mesmo código que o manager/hook lança, para a mensagem não depender de
 * onde a falha apareceu.
 */
import { isValidCpf } from './cpf'

/** Motivo de recusa de um nome, na ordem em que as regras são avaliadas. */
export type NameRule = 'REQUIRED' | 'INVALID_CHARS' | 'FORMAT' | 'LENGTH'

export type EmailError =
  | 'EMAIL_REQUIRED'
  | 'EMAIL_TOO_LONG'
  | 'EMAIL_AT_COUNT'
  | 'EMAIL_INVALID_CHARS'
  | 'EMAIL_LOCAL_INVALID'
  | 'EMAIL_DOMAIN_INVALID'

export type FieldError =
  | 'CPF_INVALID'
  | `GIVEN_NAME_${NameRule}`
  | `FAMILY_NAME_${NameRule}`
  | `MOTHER_NAME_${NameRule}`
  | 'EMAIL_INVALID'
  | EmailError
  | 'DATE_OF_BIRTH_REQUIRED'
  | 'DATE_OF_BIRTH_FUTURE'
  | 'GENDER_REQUIRED'
  | 'PHONE_INVALID'
  | 'PASSWORD_WEAK'
  | 'PASSWORD_MISMATCH'
  | 'CAPTCHA_REQUIRED'

/*
 * Regras de nome e e-mail do autocadastro (.specs/features/signup-field-validation).
 * O mesmo contrato existe em auth0/actions/lib/fieldValidation.js e, só para
 * e-mail, em workers/account-api/src/emailRule.ts; os vetores de
 * docs/contracts/signup-field-validation.vectors.json garantem que as três
 * cheguem ao mesmo código.
 */

/** Letras latinas: Latin-1 sem × e ÷, Latin Extended-A e Latin Extended Additional. */
const LETTER = 'A-Za-zÀ-ÖØ-öø-ſḀ-ỿ'
const NAME_CHARS = new RegExp(`^[${LETTER} '\\-]*$`)
const NAME_SHAPE = new RegExp(`^[${LETTER}]+(?:[ '\\-][${LETTER}]+)*$`)
const NAME_LETTERS = new RegExp(`[${LETTER}]`, 'g')
const INVISIBLE = /[​-‍⁠﻿­]/g

export const NAME_LIMITS = {
  givenName: 60,
  familyName: 100,
  motherName: 150,
} as const

export type NameField = keyof typeof NAME_LIMITS

const NAME_ERROR_PREFIX = {
  givenName: 'GIVEN_NAME',
  familyName: 'FAMILY_NAME',
  motherName: 'MOTHER_NAME',
} as const

/**
 * Só o que é cosmético: NFC, invisíveis, apóstrofo e hífen tipográficos (o
 * `´` do teclado ABNT2 incluído) e espaços. Dígito ou símbolo não some —
 * vira erro, para o usuário ver o que será gravado.
 */
export function normalizePersonName(value: string): string {
  return String(value ?? '')
    .normalize('NFC')
    .replace(INVISIBLE, '')
    .replace(/[‘’ʼ´`]/g, "'")
    .replace(/[‐-―−]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/\s*([-'])\s*/g, '$1')
    .trim()
}

/**
 * Inicial de cada palavra em maiúscula e o resto em minúscula, partículas
 * incluídas ("Silva Do Nascimento"). Palavra começa depois de espaço, hífen ou
 * apóstrofo. Não muda o tamanho do texto, então pode rodar a cada tecla.
 * Texto com dígito ou símbolo fica como veio: o erro de caractere inválido
 * mostra o que a pessoa digitou.
 */
export function toNameCase(value: string): string {
  if (!NAME_CHARS.test(value)) return value
  return value
    .toLocaleLowerCase('pt-BR')
    .replace(/(^|[\s'-])(\p{L})/gu, (_, before: string, letter: string) =>
      before + letter.toLocaleUpperCase('pt-BR'),
    )
}

/** Primeira regra violada pelo nome, ou `null`. Vazio só é erro se `required`. */
export function personNameRule(
  value: string,
  max: number,
  required = true,
): NameRule | null {
  const name = normalizePersonName(value)
  if (!name) return required ? 'REQUIRED' : null
  if (!NAME_CHARS.test(name)) return 'INVALID_CHARS'
  if (!NAME_SHAPE.test(name)) return 'FORMAT'
  const letters = name.match(NAME_LETTERS)?.length ?? 0
  if (letters < 2 || name.length > max) return 'LENGTH'
  return null
}

/** Nome da mãe é opcional; Nome e Sobrenome, obrigatórios. */
export function validatePersonName(
  value: string,
  field: NameField,
): FieldError | null {
  const rule = personNameRule(value, NAME_LIMITS[field], field !== 'motherName')
  return rule ? `${NAME_ERROR_PREFIX[field]}_${rule}` : null
}

/**
 * Nome da mãe é opcional: vazio registra que não foi informado (SMP-01..03).
 * Preenchido, segue as mesmas regras dos outros nomes.
 */
export function validateMotherName(value: string): FieldError | null {
  const rule = personNameRule(value, NAME_LIMITS.motherName, false)
  return rule ? `MOTHER_NAME_${rule}` : null
}

/** `hasMotherName` que vai ao cadastro: `yes` só com o nome preenchido. */
export function hasMotherNameFor(value: string): 'yes' | 'no' {
  return normalizePersonName(value) ? 'yes' : 'no'
}

export const EMAIL_MAX_LENGTH = 254

const DOMAIN_LABEL = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/

export function normalizeEmail(value: string): string {
  return String(value ?? '')
    .normalize('NFC')
    .replace(INVISIBLE, '')
    .trim()
    .toLowerCase()
}

/** E-mail enquanto se digita: sem espaço nem invisível, tudo minúsculo. */
export function sanitizeEmailInput(value: string): string {
  return value.replace(INVISIBLE, '').replace(/\s+/g, '').toLowerCase()
}

/** RFC 5321/5322/1035 num subconjunto prático: ASCII, um `@`, sem aspas nem IP. */
export function validateEmail(value: string): EmailError | null {
  const email = normalizeEmail(value)
  if (!email) return 'EMAIL_REQUIRED'
  if (email.length > EMAIL_MAX_LENGTH) return 'EMAIL_TOO_LONG'
  if (email.split('@').length !== 2) return 'EMAIL_AT_COUNT'
  if (!/^[a-z0-9._+@-]+$/.test(email)) return 'EMAIL_INVALID_CHARS'
  const [local, domain] = email.split('@')
  if (
    !local ||
    local.length > 64 ||
    local.startsWith('.') ||
    local.endsWith('.') ||
    local.includes('..')
  ) {
    return 'EMAIL_LOCAL_INVALID'
  }
  const labels = domain.split('.')
  const tld = labels[labels.length - 1]
  if (
    labels.length < 2 ||
    !labels.every((label) => label.length <= 63 && DOMAIN_LABEL.test(label)) ||
    !/^[a-z]{2,63}$/.test(tld)
  ) {
    return 'EMAIL_DOMAIN_INVALID'
  }
  return null
}

/** E-mail aceito pelas regras do cadastro. */
export function isPlausibleEmail(value: string): boolean {
  return validateEmail(value) === null
}

/** Celular BR: 10-11 dígitos nacionais (DDD + número), com ou sem +55. */
export function normalizeBrPhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, '')
  const national =
    digits.startsWith('55') && digits.length >= 12 ? digits.slice(2) : digits
  if (national.length < 10 || national.length > 11) return null
  return national
}

/** Data de nascimento no formato do input date (YYYY-MM-DD). */
export function validateDateOfBirth(
  value: string,
  today = new Date(),
): FieldError | null {
  if (!value.trim()) return 'DATE_OF_BIRTH_REQUIRED'
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return 'DATE_OF_BIRTH_REQUIRED'
  const todayIso = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-')
  if (value > todayIso) return 'DATE_OF_BIRTH_FUTURE'
  return null
}

export type SignupIdentityInput = {
  cpf: string
  givenName: string
  familyName: string
  email: string
  phone: string
  dateOfBirth: string
  gender: string
  motherName: string
  hasMotherName: 'yes' | 'no'
}

/**
 * Dados cadastrais comuns ao `signup` (S4) e ao `signup-id` (S5).
 * Devolve o primeiro erro na ordem visual dos campos.
 */
export function validateSignupIdentity(
  input: SignupIdentityInput,
  today = new Date(),
): FieldError | null {
  if (!isValidCpf(input.cpf)) return 'CPF_INVALID'
  const nameError =
    validatePersonName(input.givenName, 'givenName') ??
    validatePersonName(input.familyName, 'familyName')
  if (nameError) return nameError
  const emailError = validateEmail(input.email)
  if (emailError) return emailError
  if (!normalizeBrPhone(input.phone)) return 'PHONE_INVALID'
  const dateError = validateDateOfBirth(input.dateOfBirth, today)
  if (dateError) return dateError
  if (!input.gender.trim()) return 'GENDER_REQUIRED'
  if (input.hasMotherName === 'yes' && !input.motherName.trim()) {
    return 'MOTHER_NAME_REQUIRED'
  }
  return validatePersonName(input.motherName, 'motherName')
}

export const PASSWORD_MIN_LENGTH = 8

/** Senha + confirmação (S4 e S6). */
export function validateNewPassword(
  password: string,
  confirm: string,
  minLength = PASSWORD_MIN_LENGTH,
): FieldError | null {
  if (password.length < minLength) return 'PASSWORD_WEAK'
  if (password !== confirm) return 'PASSWORD_MISMATCH'
  return null
}

/** Código de acesso sem senha (SMS/OTP): 6 dígitos. */
export function isValidAccessCode(code: string, length = 6): boolean {
  return new RegExp(`^\\d{${length}}$`).test(code)
}
